import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from 'components/ui/button';
import InfoAlert from './infoAlert';
import {
  ConfigurationInputType,
  PAYMENT_CONFIG,
} from 'state/paymentConfig/type';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import { useForm } from 'react-hook-form';
import { PaymentSolutionTab } from 'state/paymentConfig/type';
import ConfigInput from './configInput';
import ConfigPassword from './configPassword';
import { useDebounce } from '@react-hook/debounce';
import ConfigSelect from './configSelect';
import ConfigToggle from './configToggle';
import ConfigAmount from './configAmount';
import ConfigPreTipping from './configPreTipping';
import ChannelConfigurations, {
  ChannelConfigHandle,
} from 'views/settings/channelConfig/ChannelConfigurations';
import { Banner } from 'components/ui/banner';
import { DEFAULT_PROCESSOR } from 'utils/constants';
import PageHeading from 'components/heading';
import {
  getConfigFieldTooltip,
  showsTerminalOverrideNotice,
} from 'constants/settings/paymentConfig';

interface IProps {
  defaultProvider: PAYMENT_CONFIG.IProcessors | undefined;
  activeTab: PaymentSolutionTab;
  localTab: PaymentSolutionTab;
}

const DEFAULT = 'DEFAULT';

export const encryptedElementFinder = (
  list: PAYMENT_CONFIG.IProcessorConfiguration[],
  code: string
) => {
  return list?.find(
    (item) =>
      item.code === code && item.inputType === ConfigurationInputType.PASSWORD
  );
};
export type FieldConfig = {
  itemConfig: PAYMENT_CONFIG.IProcessorConfiguration;
  itemValue?: {
    id: string;
    value: string;
  };
};

export const findConfig = (
  list: PAYMENT_CONFIG.IProcessorConfiguration[],
  code: string
) => {
  return list?.find((item) => item.code === code);
};

const CardReaderInput = ({ defaultProvider, activeTab, localTab }: IProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    watch,
    formState,
    control,
    setValue,
  } = useForm();
  const {
    paymentConfig,
    updateGlobalCardReaderValues,
    saveGlobalCardReaderValues,
    toCreateDefaultConfiguration,
  } = usePaymentConfig();
  const {
    getCardReaderValues,
    paymentConfiguration,
    saveCardReader,
    defaultConfigurations,
  } = paymentConfig;

  const [formChange, setOnFormChange] = useDebounce<string | null>(null, 200);
  const [disabledSave, setDisabledSave] = useState(true);
  const [elementList, setElementList] = useState<Record<string, FieldConfig>>(
    {}
  );
  const [defaultConfigurationList, setDefaultConfigurationList] = useState<
    Record<string, FieldConfig>
  >({});
  const [channelConfigDirty, setChannelConfigDirty] = useState(false);
  const channelRef = useRef<ChannelConfigHandle>(null);
  const [showUncheckWarning, setShowUncheckWarning] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);

  const saveDefaultValuesCall = useCallback(
    (values: { [x: string]: string }) => {
      const name = defaultProvider?.processorName;
      Object.keys(values).forEach((item) => {
        if (item.includes(DEFAULT)) {
          return;
        }
        // Hidden configs (e.g. tfiAsyncMode) are not user-editable on this page;
        // submitting them overwrites values set elsewhere with an empty string.
        if (
          elementList[item]?.itemConfig?.inputType ===
          ConfigurationInputType.HIDDEN
        ) {
          return;
        }
        if (values[item] !== `%${item}%magic`) {
          const { isSecret, isRequired } = elementList[item]?.itemConfig || {};
          const value = elementList?.[item]?.itemValue;
          const object = {
            id: value?.id,
            name: `${name}_${item}`,
            category: null,
            code: item,
            value: values[item].toString(),
            isSecret,
            isRequired,
            processorId: defaultProvider?.processorId,
          };

          if (value?.id) {
            updateGlobalCardReaderValues(object);
          } else {
            saveGlobalCardReaderValues(object);
          }
        }
      });
      channelRef.current?.saveChannelConfig();
    },
    [
      elementList,
      defaultProvider,
      updateGlobalCardReaderValues,
      saveGlobalCardReaderValues,
    ]
  );

  const elementCreator = useCallback(
    (
      config: PAYMENT_CONFIG.IProcessorConfiguration,
      value?: string | null,
      onChangeCallback?: (value: string) => void,
      disabled?: boolean
    ) => {
      switch (config.inputType) {
        case ConfigurationInputType.TEXT:
          return <ConfigInput config={config} register={register} />;
        case ConfigurationInputType.PASSWORD:
          return <ConfigPassword config={config} register={register} />;
        case ConfigurationInputType.SELECT:
          return <ConfigSelect config={config} control={control} />;
        case ConfigurationInputType.TOGGLE:
          return (
            <ConfigToggle
              config={config}
              control={control}
              onChange={onChangeCallback}
              value={value}
              disabled={disabled}
            />
          );
        case ConfigurationInputType.Amount:
          return (
            <ConfigAmount
              config={config}
              control={control}
              tooltip={getConfigFieldTooltip(
                defaultProvider?.processorName,
                config.code
              )}
            />
          );
        default:
          return null;
      }
    },
    [getCardReaderValues, register, control, defaultProvider?.processorName]
  );

  const createDefaultProcessorConfig = useCallback((): FieldConfig | null => {
    if (!defaultProvider) return null;

    const name = defaultProvider.paymentMethod?.includes(DEFAULT)
      ? defaultProvider.paymentMethod
      : `${DEFAULT}_${defaultProvider.paymentMethod}`;

    const defaultProcessorConfig: PAYMENT_CONFIG.IProcessorConfiguration = {
      code: name,
      category: DEFAULT_PROCESSOR,
      isSecret: false,
      isRequired: true,
      inputType: ConfigurationInputType.TOGGLE,
      displayName: 'Set to Default Provider',
      description: `Establishes this provider as your primary provider for this payment type for POS and Virtual Terminal.`,
    };

    const isDefaultProcessor = defaultConfigurations?.data?.find(
      (config) =>
        config.code === defaultProcessorConfig.code &&
        config.processorId === defaultProvider.processorId
    );

    return {
      itemConfig: defaultProcessorConfig,
      itemValue: isDefaultProcessor
        ? {
            id: isDefaultProcessor.id || '',
            value: 'true',
          }
        : {
            id: '',
            value: 'false',
          },
    };
  }, [defaultProvider, defaultConfigurations]);

  const handleDefaultConfigurationChange = useCallback(
    async (config: PAYMENT_CONFIG.IProcessorConfiguration, value: string) => {
      if (value === 'false') {
        setShowUncheckWarning(true);
        return;
      }
      if (!defaultProvider?.processorId || !defaultProvider?.processorName) {
        return;
      }
      // Guard against rapid repeat clicks while the request is in flight —
      // each successful create surfaces a banner, so unguarded clicks stack toasts.
      if (isSettingDefault) {
        return;
      }
      const defaultConfigObj: PAYMENT_CONFIG.IDefaultConfiguration = {
        name: config.code,
        category: config.category || config.code,
        processorId: defaultProvider.processorId,
        code: config.code,
        value: defaultProvider.processorName,
        isSecret: config.isSecret,
        isRequired: config.isRequired,
      };
      setIsSettingDefault(true);
      try {
        await toCreateDefaultConfiguration(defaultConfigObj);
      } catch (error) {
        console.error('Failed to create default configuration:', error);
      } finally {
        setIsSettingDefault(false);
      }
    },
    [defaultProvider, toCreateDefaultConfiguration, isSettingDefault]
  );

  const getEmptyFieldCheck = useCallback(() => {
    return paymentConfiguration?.configurations?.some(
      (item) => item.isRequired && !getValues(item.code)
    );
  }, [paymentConfiguration, getValues]);

  const getTipElement = () => {
    if (elementList?.ffdTippingEnabled) {
      return (
        <ConfigPreTipping
          config={elementList?.ffdTippingEnabled?.itemConfig}
          registerProp={register}
          default_value={
            elementList?.ffdTippingEnabled?.itemValue?.value || 'false'
          }
          control={control}
          setValueProp={setValue}
          percentValue={
            elementList?.[
              elementList?.ffdTippingEnabled?.itemConfig?.inputOptions
                ?.percentValuesCode || ''
            ]
          }
          watch={watch}
        />
      );
    }
  };

  useEffect(() => {
    setElementList({});
    let tempList: Record<string, FieldConfig> = {};
    if (paymentConfiguration?.configurations && activeTab === localTab) {
      const nonToggleConfigs: Record<string, FieldConfig> = {};
      const toggleConfigs: Record<string, FieldConfig> = {};

      paymentConfiguration.configurations.forEach((item) => {
        if (item.inputType === ConfigurationInputType.TOGGLE) {
          toggleConfigs[item.code] = { itemConfig: item };
        } else {
          nonToggleConfigs[item.code] = { itemConfig: item };
        }
      });

      // Merge non-toggle and toggle configs into a single object
      tempList = { ...nonToggleConfigs, ...toggleConfigs };
    }

    if (
      Object.keys(tempList).length > 0 &&
      getCardReaderValues?.items &&
      activeTab === localTab
    ) {
      getCardReaderValues.items.forEach((item) => {
        const itemConfig = tempList[item.code]?.itemConfig;
        if (itemConfig) {
          tempList[item.code] = {
            itemConfig: {
              ...itemConfig,
              isSecret: itemConfig?.isSecret ?? item?.isSecret,
              isRequired: itemConfig?.isRequired ?? item?.isRequired,
            },
            itemValue: {
              id: item.id,
              value: encryptedElementFinder(
                paymentConfiguration?.configurations,
                item.code
              )
                ? `%${item.code}%magic`
                : item.value,
            },
          };
        }
        // setElementList({ ...tempList });
      });
      setElementList(tempList);
      reset(
        Object.keys(tempList).reduce((_prev, _key) => {
          const item = tempList[_key];
          const inputType = item.itemConfig?.inputType;

          // Set default values based on inputType if neither value nor defaultValue exists

          const typeBasedValue = (type: ConfigurationInputType) => {
            switch (type) {
              case ConfigurationInputType.TEXT:
              case ConfigurationInputType.PASSWORD:
              case ConfigurationInputType.SELECT:
                return (
                  item.itemValue?.value || item.itemConfig?.defaultValue || ''
                );
              case ConfigurationInputType.TOGGLE:
              case ConfigurationInputType.TIPPING_TOGGLE:
                return (
                  item.itemValue?.value ||
                  item.itemConfig?.defaultValue ||
                  'false'
                );
              case ConfigurationInputType.Amount:
                return parseInt(
                  item.itemValue?.value || item.itemConfig?.defaultValue || '0'
                );
              case ConfigurationInputType.HIDDEN:
                return (
                  item.itemValue?.value || item.itemConfig?.defaultValue || ''
                );
              default:
                return '';
            }
          };

          _prev[_key] = typeBasedValue(inputType);
          return _prev;
        }, {} as Record<string, unknown>)
      );
    }
  }, [paymentConfiguration, getCardReaderValues, activeTab, localTab]);

  useEffect(() => {
    if (
      activeTab === localTab &&
      (Object.keys(errors).length > 0 ||
        getEmptyFieldCheck() ||
        !defaultProvider ||
        (Object.keys(formState.dirtyFields).length === 0 &&
          !channelConfigDirty))
    ) {
      setDisabledSave(true);
    } else {
      setDisabledSave(false);
    }
  }, [
    errors,
    defaultProvider,
    activeTab,
    formChange,
    formState,
    getEmptyFieldCheck,
    channelConfigDirty,
  ]);

  useEffect(() => {
    if (saveCardReader) {
      setDisabledSave(true);
    }
  }, [saveCardReader]);

  useEffect(() => {
    const tempDefaultList: Record<string, FieldConfig> = {};

    if (!(activeTab === localTab && defaultProvider)) {
      return;
    }

    // Add default processor configuration
    const defaultProcessorFieldConfig = createDefaultProcessorConfig();
    if (defaultProcessorFieldConfig) {
      tempDefaultList[defaultProcessorFieldConfig.itemConfig.code] =
        defaultProcessorFieldConfig;
    }

    if (paymentConfiguration?.defaultConfigurations) {
      paymentConfiguration.defaultConfigurations.forEach((item) => {
        // Check if this processor is currently set as default for this configuration
        const isCurrentlyDefault = defaultConfigurations?.data?.find(
          (config) =>
            config.code === item.code &&
            config.processorId === defaultProvider.processorId
        );

        tempDefaultList[item.code] = {
          itemConfig: item,
          itemValue: isCurrentlyDefault
            ? {
                id: isCurrentlyDefault.id || '',
                value: 'true',
              }
            : {
                id: '',
                value: 'false',
              },
        };
      });
    }
    setDefaultConfigurationList(tempDefaultList);

    // Initialize form values for default configurations if not already set
    const defaultConfigValues = Object.keys(tempDefaultList).reduce(
      (_prev, _key) => {
        const item = tempDefaultList[_key];
        _prev[_key] = item.itemValue?.value || 'false';
        return _prev;
      },
      {} as Record<string, unknown>
    );

    const currentValues = getValues();
    reset({
      ...currentValues,
      ...defaultConfigValues,
    });
  }, [
    paymentConfiguration,
    activeTab,
    localTab,
    defaultConfigurations,
    defaultProvider,
    createDefaultProcessorConfig,
    getCardReaderValues,
  ]);

  return (
    <div>
      <PageHeading level="h2" heading="Global configuration" className="my-3" />
      {localTab === activeTab && (
        <div>
          {Object.keys(elementList).length > 0 ? (
            <>
              {activeTab !== PaymentSolutionTab.ACH &&
                showsTerminalOverrideNotice(defaultProvider?.processorName) && (
                  <InfoAlert>
                    These defaults can be overridden for individual terminals
                    under Payment device configurations.
                  </InfoAlert>
                )}
              <form
                role="form"
                onSubmit={handleSubmit(saveDefaultValuesCall)}
                onChange={() => setOnFormChange(new Date().toString())}
                className="mt-2"
              >
                {Object.keys(elementList).map((item) =>
                  elementCreator(elementList[item].itemConfig, null)
                )}
                {defaultProvider && (
                  <ChannelConfigurations
                    processorId={defaultProvider?.processorId}
                    processorName={defaultProvider?.processorName}
                    onDirtyChange={setChannelConfigDirty}
                    ref={channelRef}
                  />
                )}
                {getTipElement()}
                <Button
                  data-testid="save_values"
                  variant="default"
                  size="default"
                  type="submit"
                  className="flex items-center m-0 mb-2 w-[162px]"
                  disabled={disabledSave}
                >
                  Save
                </Button>
                <div />
                <div className="mb-3 mr-3 mt-3">
                  <span className="text-gray-blue-80 font-semibold text-base">
                    Default configuration
                  </span>
                </div>
                <span>
                  {Object.keys(defaultConfigurationList).map((key) => {
                    const config = defaultConfigurationList[key].itemConfig;
                    return elementCreator(
                      config,
                      defaultConfigurationList[key].itemValue?.value,
                      (value: string) =>
                        handleDefaultConfigurationChange(config, value),
                      isSettingDefault
                    );
                  })}
                </span>
              </form>
            </>
          ) : (
            <InfoAlert>
              Something went wrong. Please contact your TreezPay Account
              Manager or
              <a
                className="ml-1 text-dark-green-link underline hover:no-underline"
                target="_blank"
                rel="noopener noreferrer"
                href="https://support.treez.io/hc/en-us/requests/new"
              >
                contact support
              </a>{' '}
              in case there are any queries.
            </InfoAlert>
          )}
        </div>
      )}
      {showUncheckWarning && (
        <div className="mt-4 max-w-3xl">
          <Banner
            variant="error"
            message="Cannot uncheck default configuration. Please select a different processor as default first."
            showClose
            onClose={() => setShowUncheckWarning(false)}
          />
        </div>
      )}
    </div>
  );
};

export default CardReaderInput;
